import { useState, useEffect } from "react";
import MaterialErrorCard from "./MaterialErrorCard";

function MaterialWrapper({ children, fallback = null, onError = null, customTheme = null, onClose = null, errorVariant = "panel" }) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [MaterialComponents, setMaterialComponents] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let mounted = true;

    const loadMaterialLibrary = async () => {
      try {
        const [{ ThemeProvider, createTheme }, { ScopedCssBaseline }] = await Promise.all([
          import("@mui/material/styles"),
          import("@mui/material")
        ]);

        const theme =
          customTheme ||
          createTheme({
            palette: {
              mode: "light",
              primary: {
                main: "rgba(202, 36, 32, 0.85)",
              },
              secondary: {
                main: "#00A9E3",
              },
            },
          });

        if (mounted) {
          setMaterialComponents({
            ThemeProvider,
            theme,
            ScopedCssBaseline,
          });
          setIsLoading(false);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err);
          setIsLoading(false);
          if (onError) onError(err);
        }
      }
    };

    setIsLoading(true);
    setError(null);
    loadMaterialLibrary();

    return () => {
      mounted = false;
    };
  }, [customTheme, onError, retryKey]);

  const handleRetry = () => {
    setIsLoading(true);
    setError(null);
    setRetryKey((k) => k + 1);
  };

  if (isLoading) {
    return (
      fallback || (
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading Material-UI...</span>
          </div>
        </div>
      )
    );
  }

  if (error) {
    return <MaterialErrorCard error={error} onRetry={handleRetry} onClose={onClose} variant={errorVariant} />;
  }

  if (!MaterialComponents) {
    return null;
  }

  const { ThemeProvider, theme, ScopedCssBaseline } = MaterialComponents;

  return (
    <ThemeProvider theme={theme}>
      <ScopedCssBaseline />
      {children}
    </ThemeProvider>
  );
}

export default MaterialWrapper;
